import { memo, useRef, useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import { Card, CardContent } from '@mui/material';

import { PageAlertMessage } from 'src/diamond-components';
import { usePolicyInformationLookups } from 'src/api/hooks/usePolicyQueries';

import { PolicyInformationEdit } from './policy-information-edit';
import { PolicyInformationView } from './policy-information-view';
import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import {
  validateDraft,
  hasFieldErrors,
  hasDraftChanges,
  initializeDraft,
  findCodeByDescription,
  buildPolicySectionPayload,
  buildStatePremiumsPayload,
  findStateCodeByAbbreviation,
  buildChangedFieldSummaryLines,
  getPolicyInformationDraftDelta,
  applyPolicyInformationDraftDelta,
} from './policy-information.helpers';

import type {
  PolicyEditDraft,
  PolicyFieldErrors,
  PolicyInformationProps,
  PolicyInformationHandle,
} from './policy-information.types';

export type { PolicyInformationHandle } from './policy-information.types';

const EMPTY_LOOKUPS = {
  stateLookups: [],
  marketLookups: [],
  legalNatureLookups: [],
  policyTypeLookups: [],
  policyPlanTypeLookups: [],
  retroRatingLookups: [],
  experienceRatingLookups: [],
};

const PolicyInformation = forwardRef<PolicyInformationHandle, PolicyInformationProps>(
  ({ comboId, viewPolicy, isEditMode, initialDraftDelta = null, onDraftDeltaChange }, ref) => {
    const headerWidth = useHeaderWidth();
    // Lazy initializer runs exactly once per real mount (unaffected by Strict Mode's mount-effect
    // double-invocation), so the cached delta is merged in before first paint instead of via an
    // effect whose "already applied" ref would otherwise be wiped by that double-invocation.
    const [draft, setDraft] = useState<PolicyEditDraft>(() =>
      applyPolicyInformationDraftDelta(initializeDraft(viewPolicy), initialDraftDelta)
    );
    const [snapshot, setSnapshot] = useState<PolicyEditDraft | null>(null);
    const [fieldErrors, setFieldErrors] = useState<PolicyFieldErrors>({});
    const [apiError, setApiError] = useState<string>('');
    const [isLookupErrorDismissed, setIsLookupErrorDismissed] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const { data: lookupData, isError: hasLookupLoadError } = usePolicyInformationLookups();
    const lookups = useMemo(() => lookupData ?? EMPTY_LOOKUPS, [lookupData]);
    // Tracks the last viewPolicy this component reset for, so the reset effect only fires on a
    // genuine data change (e.g. post-save refetch), not on an unchanged-deps re-invocation.
    const previousViewPolicyRef = useRef(viewPolicy);

    const {
      stateLookups,
      marketLookups,
      legalNatureLookups,
      policyTypeLookups,
      policyPlanTypeLookups,
      retroRatingLookups,
    } = lookups;

    useEffect(() => {
      if (previousViewPolicyRef.current === viewPolicy) {
        return;
      }
      previousViewPolicyRef.current = viewPolicy;
      setDraft(initializeDraft(viewPolicy));
      setSnapshot(null);
      setFieldErrors({});
      setApiError('');
    }, [viewPolicy]);

    useEffect(() => {
      onDraftDeltaChange?.(getPolicyInformationDraftDelta(snapshot ?? initializeDraft(viewPolicy), draft));
    }, [draft, snapshot, viewPolicy, onDraftDeltaChange]);

    useEffect(() => {
      if (isEditMode) {
        setSnapshot((current) => current ?? draft);
        setIsLookupErrorDismissed(false);
        return;
      }

      setSnapshot(null);
    }, [draft, isEditMode]);

    useEffect(() => {
      if (!hasLookupLoadError) {
        setIsLookupErrorDismissed(false);
      }
    }, [hasLookupLoadError]);


    useEffect(() => {
      if (stateLookups.length === 0) {
        return;
      }

      // Backfill snapshot alongside draft so lookup-derived defaults never register as edits.
      const applyMinimumPremiumStateCode = (current: PolicyEditDraft) => ({
        ...current,
        minimumPremiumStateCode:
          current.minimumPremiumStateCode > 0
            ? current.minimumPremiumStateCode
            : findStateCodeByAbbreviation(stateLookups, viewPolicy.minimumPremiumState),
      });

      setDraft(applyMinimumPremiumStateCode);
      setSnapshot((current) => (current ? applyMinimumPremiumStateCode(current) : current));
    }, [stateLookups, viewPolicy]);

    useEffect(() => {
      if (
        legalNatureLookups.length === 0 &&
        retroRatingLookups.length === 0 &&
        policyTypeLookups.length === 0 &&
        policyPlanTypeLookups.length === 0 &&
        marketLookups.length === 0
      ) {
        return;
      }

      // Backfill snapshot alongside draft so lookup-derived defaults never register as edits.
      const applyCodeDefaults = (current: PolicyEditDraft) => {
        const next = { ...current };

        if (!next.legalNatureCode) {
          next.legalNatureCode = findCodeByDescription(
            legalNatureLookups,
            viewPolicy.legalNatureEnumDescription
          );
        }

        if (!next.retroRatingCode) {
          next.retroRatingCode = findCodeByDescription(
            retroRatingLookups,
            viewPolicy.retroRatingTypeEnumDescription
          );
        }

        if (!next.policyTypeCode) {
          next.policyTypeCode = findCodeByDescription(
            policyTypeLookups,
            viewPolicy.policyTypeEnumDescription
          );
        }

        if (!next.planTypeCode) {
          next.planTypeCode =
            findCodeByDescription(policyPlanTypeLookups, viewPolicy.planTypeEnumDescription) ||
            findCodeByDescription(
              marketLookups.map((item) => ({ code: item.code, description: item.description })),
              viewPolicy.planTypeEnumDescription
            );
        }

        return next;
      };

      setDraft(applyCodeDefaults);
      setSnapshot((current) => (current ? applyCodeDefaults(current) : current));
    }, [
      legalNatureLookups,
      marketLookups,
      policyPlanTypeLookups,
      policyTypeLookups,
      retroRatingLookups,
      viewPolicy,
    ]);

    const displayErrorMessage =
      apiError ||
      (isEditMode && hasLookupLoadError && !isLookupErrorDismissed
        ? 'Unable to load one or more policy edit lookup lists.'
        : '');

    const validateForSave = (): boolean => {
      const errors = validateDraft(draft);
      if (hasFieldErrors(errors)) {
        setFieldErrors(errors);
        return false;
      }

      setFieldErrors({});
      return true;
    };

    const hasPendingChanges = (): boolean => {
      if (!snapshot) {
        return false;
      }

      return hasDraftChanges(snapshot, draft);
    };

    const getChangedFieldSummaries = (): string[] => {
      if (!snapshot) {
        return [];
      }

      return buildChangedFieldSummaryLines(snapshot, draft, {
        stateLookups,
        legalNatureLookups,
        retroRatingLookups,
        policyTypeLookups,
        policyPlanTypeLookups,
        marketLookups,
      });
    };

    const cancelEdit = () => {
      if (snapshot) {
        setDraft(snapshot);
      }
      setFieldErrors({});
      setApiError('');
    };

    const commitEdit = () => {
      setSnapshot(draft);
      setApiError('');
      setSuccessMessage('');
    };

    useImperativeHandle(ref, () => ({
      validateForSave,
      hasPendingChanges,
      getChangedFieldSummaries,
      getPolicySectionPayload: () => buildPolicySectionPayload(draft),
      getStatePremiumsPayload: () => buildStatePremiumsPayload(snapshot ?? draft, draft),
      commitEdit,
      cancelEdit,
    }));

    const marketOptions = marketLookups.length > 0
      ? marketLookups.map((item) => ({ code: item.code, description: item.description }))
      : policyPlanTypeLookups;

    return (
      <Card
        sx={[
          () => ({
            display: 'flex',
            zIndex: 'unset',
            overflow: 'unset',
            mt: 5,
            borderRadius: 2,
            boxShadow: 'var(--customShadows-card)',
            width: headerWidth,
          }),
        ]}
      >
        <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="policy-information-card">
          <PageAlertMessage
            message={displayErrorMessage}
            onClose={() => {
              if (apiError) {
                setApiError('');
                return;
              }

              setIsLookupErrorDismissed(true);
            }}
            open={Boolean(displayErrorMessage)}
            position="top-center"
            severity="error"
            testId="policy-edit-error"
          />

          <PageAlertMessage
            autoHideDuration={4000}
            message={successMessage}
            onClose={() => setSuccessMessage('')}
            open={Boolean(successMessage)}
            position="bottom-right"
            severity="success"
            testId="policy-edit-success"
          />

          {isEditMode ? (
            <PolicyInformationEdit
              comboId={comboId}
              viewPolicy={viewPolicy}
              draft={draft}
              setDraft={setDraft}
              stateLookups={stateLookups}
              legalNatureLookups={legalNatureLookups}
              retroRatingLookups={retroRatingLookups}
              policyTypeLookups={policyTypeLookups}
              marketOptions={marketOptions}
              fieldErrors={fieldErrors}
            />
          ) : (
            <PolicyInformationView comboId={comboId} viewPolicy={viewPolicy} />
          )}

        </CardContent>
      </Card>
    );
  }
);

PolicyInformation.displayName = 'PolicyInformation';

export default memo(PolicyInformation);
